import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_ACTION = 'https://readdy.ai/api/form/dbadvbm9if4sq287d14g';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === 'company_alt') return;
      const text = typeof value === 'string' ? value : value.name;
      if (text.trim()) body.append(key, text);
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === 'string' && serverMessage.includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!ok) {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请稍后重试。'
            : serverMessage || '提交失败，请稍后重试。',
        );
        return;
      }

      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试。');
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
      <h2 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h2>
      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
        欢迎提供影片资料勘误、片单建议，或只是想聊聊最近在看什么。
      </p>

      {status === 'success' ? (
        <div className="mt-5 flex items-start gap-2.5 rounded-md bg-secondary-100 px-3.5 py-3">
          <i className="ri-checkbox-circle-line mt-0.5 text-secondary-700 w-4 h-4 flex items-center justify-center" />
          <div>
            <p className="text-sm font-medium text-secondary-900">留言已提交</p>
            <p className="mt-1 text-xs leading-relaxed text-secondary-900/80">
              感谢你的反馈，我们会在整理后回复。你也可以继续浏览其他影视内容。
            </p>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="mt-2 text-xs font-medium text-secondary-900 underline cursor-pointer"
            >
              再写一条
            </button>
          </div>
        </div>
      ) : (
        <form id="about-message-form" data-readdy-form onSubmit={handleSubmit} className="mt-4 space-y-3.5">
          <input
            type="text"
            name="company_alt"
            className="field-hide"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                称呼
              </label>
              <input
                id="contact-name"
                name="name"
                required
                maxLength={40}
                placeholder="怎么称呼你"
                className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                maxLength={80}
                placeholder="you@example.com"
                className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-topic" className="mb-1.5 block text-xs font-medium text-foreground-700">
              留言主题
            </label>
            <select
              id="contact-topic"
              name="topic"
              defaultValue="剧情勘误"
              className="h-10 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50 cursor-pointer"
            >
              <option value="剧情勘误">剧情勘误</option>
              <option value="片单建议">片单建议</option>
              <option value="合作咨询">合作咨询</option>
              <option value="其他反馈">其他反馈</option>
            </select>
          </div>
          <div>
            <label htmlFor="contact-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
              留言内容
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={4}
              maxLength={500}
              placeholder="想说的话写在这里（500 字以内）"
              className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
            />
          </div>

          {status === 'error' && formError && (
            <p className="flex items-start gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800">
              <i className="ri-error-warning-line mt-0.5 w-3.5 h-3.5 flex items-center justify-center" />
              {formError}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <p className="text-[11px] text-foreground-500">提交即表示同意我们保存你的留言内容。</p>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex h-10 items-center gap-1.5 rounded-md bg-accent-500 px-5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
            >
              {status === 'submitting' ? (
                <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
              ) : (
                <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
              )}
              {status === 'submitting' ? '提交中' : '提交留言'}
            </button>
          </div>
        </form>
      )}

      <p className="mt-4 border-t border-background-200 pt-3 text-[11px] text-foreground-500">
        也可以直接发邮件到 hello@tongxuedemama.site（演示地址），我们通常在三日内回复。
      </p>
      <Link
        to="/"
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary-600 hover:underline cursor-pointer"
      >
        返回首页继续浏览
        <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center" />
      </Link>
    </div>
  );
}